/**
 *
 *  Basic Acequia functions
 *
 */

import { config } from '../config.js'
export { config }

import utils, { isBrowser, isServiceWorker, isNode, isDeno } from './utils.js'
import { normalizeBody } from './requestNormalize.js'
import { prepareProxiedBody, startProgressPings, postedBodyReadable, bodyLostResponse } from './localDispatch.js'
import { moduleServiceWorkerSupport, classifyRegistrationFailure } from './swSupport.js'

// Static imports for browser (bundled by rollup)
// initRuntime() handles Node.js/Deno deps without top-level await
import { jose, localforage as localforageModule, initRuntime } from '../deps.js'
import { watchStore } from './storageWatchdog.js'

export * as routes from './routes.js'

// Set up storage - browser/SW path is synchronous (no top-level await needed)
// Node.js/Deno path deferred to initStore() to avoid top-level await in SW
let acequiaStore

if (!isDeno() && !isNode()) {
    // Browser/SW: use localforage (IndexedDB) - synchronous setup
    acequiaStore = watchStore(localforageModule.createInstance({ name: 'acequia' }), 'acequia')
}

/**
 * Initialize storage for Node.js/Deno contexts.
 * Called from ensureGUIDs() before first store access.
 * No-op if store already initialized (browser/SW).
 */
async function initStore() {
    if (acequiaStore) return
    await initRuntime()
    if (isDeno()) {
        const { createFilesystemStore } = await import('./deno/storage.js')
        const storageRoot = Deno.env.get('ACEQUIA_STORAGE') || './acequia-data'
        acequiaStore = createFilesystemStore(storageRoot, 'acequia')
    } else if (isNode()) {
        const { createFilesystemStore } = await import('./node/storage.js')
        const storageRoot = process.env.ACEQUIA_STORAGE || './acequia-data'
        acequiaStore = createFilesystemStore(storageRoot, 'acequia')
    }
}

export { acequiaStore }

// setup acequia environment

/**
 * Check Local Network Access permission status
 * @returns {Promise<string>} Permission state: "granted", "denied", "prompt", or "unsupported"
 */
async function checkLocalNetworkAccessPermission() {
    try {
        const result = await navigator.permissions.query({
            name: 'local-network-access',
        })
        return result.state
    } catch (e) {
        // Permission API doesn't support local-network-access in this browser
        return 'unsupported'
    }
}

/**
 * Trigger the LNA permission prompt by making a local network request
 */
async function triggerLocalNetworkAccessPrompt() {
    try {
        await fetch('https://localhost:65535/', {
            mode: 'no-cors',
            signal: AbortSignal.timeout(2000),
        })
    } catch (e) {
        // Expected to fail - we only need to trigger the permission prompt
    }
}

/**
 * Request Local Network Access permission from Chrome 142+
 * Must be called from main page context before service worker makes local requests.
 * Chrome's LNA feature blocks requests from public websites to private/local networks
 * unless the user grants permission. This triggers the permission prompt early.
 *
 * If a service worker is already controlling the page, it will be temporarily
 * unregistered to allow the permission prompt to appear, then re-registered.
 */
async function requestLocalNetworkAccess() {
    // Only needed in secure contexts (HTTPS)
    if (typeof window === 'undefined' || !window.isSecureContext) return

    // Check current permission status
    const permissionState = await checkLocalNetworkAccessPermission()
    console.log('Local Network Access permission state:', permissionState)

    // Only trigger prompt if permission hasn't been granted or denied
    if (permissionState === 'granted') {
        console.log('Local Network Access already granted')
        return
    }

    if (permissionState === 'denied') {
        console.log(
            'Local Network Access denied. User must enable in chrome://settings/content/localNetworkAccess'
        )
        return
    }

    // Permission state is "prompt" or "unsupported" - try to trigger the prompt
    console.log('Requesting Local Network Access permission...')

    // Check if SW is already controlling this page
    // if (navigator.serviceWorker?.controller) {
    //     console.log(
    //         'Service worker active - temporarily unregistering to trigger LNA prompt'
    //     )

    //     // Get the current registration
    //     const registration = await navigator.serviceWorker.ready

    //     // Unregister the SW
    //     await registration.unregister()
    //     console.log('Service worker unregistered')

    //     // Trigger the permission prompt
    //     await triggerLocalNetworkAccessPrompt()
    //     console.log('LNA permission prompt triggered')

    //     // Re-register the SW
    //     console.log('Re-registering service worker...')
    //     await registerServiceWorker()
    // } else {
    //     // No SW controlling, make request directly
    //     await triggerLocalNetworkAccessPrompt()
    //     console.log('LNA permission request completed')
    // }
}

// Why this page does or doesn't have the acequia SW — see
// serviceWorkerStatus(). `swOutcome` settles once registration has
// succeeded or definitively failed, so swControlled() can stop waiting.
let swStatus = { state: 'pending' }
let swOutcomeResolve
const swOutcome = new Promise((resolve) => (swOutcomeResolve = resolve))

function settleSWStatus(status) {
    swStatus = status
    swOutcomeResolve(status)
}

/**
 * Where service worker registration stands for this page:
 *   { state: 'pending' }                       not attempted / in progress
 *   { state: 'registered' }
 *   { state: 'unsupported', reason, message }  'no-service-worker' |
 *                                              'module-service-worker'
 *   { state: 'failed', reason, message }       'script-evaluation' |
 *                                              'registration-error'
 * Without a registered SW, /groups/ requests go over the network and VFS
 * mounts, the htmx layer and the content-addressed cache are absent.
 */
export function serviceWorkerStatus() {
    return swStatus
}

async function registerServiceWorker() {
    if ('serviceWorker' in navigator) {
        try {
            const registration = await navigator.serviceWorker.register(
                `${config.build_root}sw.js`,
                {
                    scope: `${config.build_root}`,
                    type: 'module',
                }
            )

            console.log('Service worker registered:', registration)
            settleSWStatus({ state: 'registered' })

            // Wait for the service worker to be ready
            await navigator.serviceWorker.ready

            // Ensure the SW is controlling this page
            if (!navigator.serviceWorker.controller) {
                console.log('SW active but not controlling - requesting claim...')

                // Ask the active SW to claim this page
                const sw = registration.active || registration.installing || registration.waiting
                if (sw) {
                    sw.postMessage({ type: 'CLAIM_CLIENTS' })
                }

                // Wait for the SW to take control (with timeout)
                await Promise.race([
                    new Promise((resolve) => {
                        navigator.serviceWorker.addEventListener(
                            'controllerchange',
                            () => {
                                console.log('Service worker now controlling page')
                                resolve()
                            },
                            { once: true }
                        )
                    }),
                    new Promise((resolve) => {
                        setTimeout(() => {
                            // The race's losing timer still fires after
                            // controllerchange won — only warn when control
                            // genuinely never arrived.
                            if (!navigator.serviceWorker.controller) {
                                console.warn(
                                    'Timeout waiting for service worker to take control. ' +
                                        'SW will control on next navigation.'
                                )
                            }
                            resolve()
                        }, 3000)
                    }),
                ])
            }

            return true
        } catch (error) {
            // A browser without module SWs fails here with the same opaque
            // "threw during script evaluation" as a broken deployment; the
            // probe tells them apart (swSupport.js).
            const moduleSupport = await moduleServiceWorkerSupport(
                navigator.serviceWorker
            ).catch(() => null)
            const status = classifyRegistrationFailure(error, moduleSupport)
            if (status.state === 'unsupported') console.warn(status.message)
            else console.error(status.message, error)
            settleSWStatus(status)
            return false
        }
    }

    const status = {
        state: 'unsupported',
        reason: 'no-service-worker',
        message:
            'Service workers are unavailable here (a private window, an ' +
            'insecure origin, or the browser), so the acequia service ' +
            'worker is not running.',
    }
    console.warn(status.message)
    settleSWStatus(status)
    return false
}

async function ensureGUID(name) {
    let id = await acequiaStore.getItem(name)
    if (!id) {
        id = await acequiaStore.setItem(name, utils.createId())
    }
    return id
}

async function ensureKeys() {
    let keys = await acequiaStore.getItem('keys')
    if (!keys) {
        const keyPair = await jose.generateKeyPair('PS256', {
            extractable: true,
            keyUsages: ['encrypt', 'decrypt', 'sign', 'verify'],
        })

        if (isNode() || isDeno()) {
            // Node.js/Deno: store as JWK for filesystem serialization
            keys = {
                publicKey: await jose.exportJWK(keyPair.publicKey),
                privateKey: await jose.exportJWK(keyPair.privateKey),
                _jwk: true,
            }
        } else {
            // Browser/SW: store CryptoKey objects directly in IndexedDB
            keys = keyPair
        }

        await acequiaStore.setItem('keys', keys)
    }

    // If stored as JWK, import back to CryptoKey
    if (keys._jwk) {
        keys = {
            publicKey: await jose.importJWK(keys.publicKey, 'PS256', { extractable: true }),
            privateKey: await jose.importJWK(keys.privateKey, 'PS256', { extractable: true }),
        }
    }

    return keys
}

/**
 * Ensure user-specific keys exist for this device.
 * Each device generates its own keypair for user identity.
 * This enables per-device key revocation while maintaining user identity.
 */
async function ensureUserKeys() {
    let userKeys = await acequiaStore.getItem('userKeys')
    if (!userKeys) {
        const keyPair = await jose.generateKeyPair('PS256', {
            extractable: true,
            keyUsages: ['sign', 'verify'],
        })

        if (isNode() || isDeno()) {
            // Node.js/Deno: store as JWK for filesystem serialization
            userKeys = {
                publicKey: await jose.exportJWK(keyPair.publicKey),
                privateKey: await jose.exportJWK(keyPair.privateKey),
                _jwk: true,
            }
        } else {
            // Browser/SW: store CryptoKey objects directly in IndexedDB
            userKeys = keyPair
        }

        await acequiaStore.setItem('userKeys', userKeys)
    }

    // If stored as JWK, import back to CryptoKey
    if (userKeys._jwk) {
        userKeys = {
            publicKey: await jose.importJWK(userKeys.publicKey, 'PS256', { extractable: true }),
            privateKey: await jose.importJWK(userKeys.privateKey, 'PS256', { extractable: true }),
        }
    }

    return userKeys
}

// ---------------------------------------------------------------------------
// Instance deduplication via BroadcastChannel
// ---------------------------------------------------------------------------
// Chrome copies sessionStorage when duplicating a tab, so two tabs can end up
// with the same instanceId. We use a BroadcastChannel to detect this: each tab
// announces its instanceId + birth timestamp. If another tab has the same ID
// and was born earlier, the newer tab regenerates.

const _instanceBirth = Date.now()
let _instanceBC = null

function _deduplicateInstance() {
    try {
        _instanceBC = new BroadcastChannel('acequia-instance-dedup')

        // Announce our instanceId
        _instanceBC.postMessage({ type: 'claim', instanceId, birth: _instanceBirth })

        _instanceBC.onmessage = async (e) => {
            const msg = e.data
            if (msg.type === 'claim' && msg.instanceId === instanceId && msg.birth < _instanceBirth) {
                // The other tab has our ID and is older — we must yield
                instanceId = utils.createId()
                sessionStorage.setItem('instanceId', instanceId)
                const { generateInstanceName } = await import('./nameGenerator.js')
                instanceName = generateInstanceName()
                sessionStorage.setItem('instanceName', instanceName)
                console.log('instanceId collision detected (tab duplication), regenerated:', instanceId, instanceName)
                // Re-announce with our new ID
                _instanceBC.postMessage({ type: 'claim', instanceId, birth: _instanceBirth })
            } else if (msg.type === 'claim' && msg.instanceId === instanceId && msg.birth > _instanceBirth) {
                // We're older — re-announce so the newer tab sees us
                _instanceBC.postMessage({ type: 'claim', instanceId, birth: _instanceBirth })
            }
        }
    } catch {
        // BroadcastChannel not available or errored — no dedup, same as before
    }
}

async function ensureGUIDs() {
    // Initialize store for Node.js/Deno (no-op if already initialized in browser/SW)
    await initStore()

    const [deviceId, userId, keys, userKeys] = await Promise.all([
        ensureGUID('deviceId'),
        ensureGUID('userId'),
        ensureKeys(),
        ensureUserKeys(),
    ])

    // Load cached handle and deviceName from store
    handle = await acequiaStore.getItem('handle')
    deviceName = await acequiaStore.getItem('deviceName')
    if (!deviceName) {
        // IndexedDB may return null during concurrent tab opens (race condition).
        // Yield briefly and retry before generating a new name that would overwrite
        // the existing one for all tabs.
        await new Promise(r => setTimeout(r, 50))
        deviceName = await acequiaStore.getItem('deviceName')
    }
    if (!deviceName) {
        const { generateDeviceName } = await import('./nameGenerator.js')
        deviceName = generateDeviceName()
        await acequiaStore.setItem('deviceName', deviceName)
    }

    // instanceId handling - session-scoped in browser, ephemeral elsewhere
    if (isBrowser() && typeof sessionStorage !== 'undefined') {
        instanceId = sessionStorage.getItem('instanceId')
        if (!instanceId) {
            instanceId = utils.createId()
            sessionStorage.setItem('instanceId', instanceId)
        }
        instanceName = sessionStorage.getItem('instanceName')
        if (!instanceName) {
            const { generateInstanceName } = await import('./nameGenerator.js')
            instanceName = generateInstanceName()
            sessionStorage.setItem('instanceName', instanceName)
        }

        // Deduplicate instanceId across tabs. Chrome copies sessionStorage on
        // tab duplication, so two tabs can start with the same instanceId.
        // Use BroadcastChannel to detect the collision: the newer tab yields.
        if (typeof BroadcastChannel !== 'undefined') {
            _deduplicateInstance()
        }
    } else {
        // SW, Web Worker, Node.js: generate ephemeral instanceId + instanceName
        instanceId = utils.createId()
        const { generateInstanceName } = await import('./nameGenerator.js')
        instanceName = generateInstanceName()
    }

    if (config.debug)
        console.log('identity: ', {
            deviceId,
            userId,
            instanceId,
            handle,
            deviceName,
            instanceName,
        })

    // Expose globals only in browser context
    if (isBrowser()) {
        window.jose = jose
        window.keys = keys
        window.userKeys = userKeys
    }

    return { deviceId, userId, keys, userKeys }
}

//  Setup deviceId and userId from indexeddb
//  This is an async process so acequia needs a ready() promise
//  To inform scripts when acequia has been set up

let deviceId = null,
    userId = null,
    handle = null,
    deviceName = null,
    keys,
    instanceId = null,
    instanceName = null,
    privateJWK = null,
    publicJWK = null,
    userKeys = null,
    userPrivateJWK = null,
    userPublicJWK = null

let resolveReady
let readyPromise = new Promise((resolve) => {
    resolveReady = resolve
})

function makeReady(status) {
    return resolveReady(status)
}

export function acequiaReady() {
    return readyPromise
}

ensureGUIDs().then(async (guids) => {
    deviceId = guids.deviceId
    userId = guids.userId
    keys = guids.keys
    userKeys = guids.userKeys

    // Export keys as JWK (works in all contexts)
    privateJWK = await jose.exportJWK(keys.privateKey)
    publicJWK = await jose.exportJWK(keys.publicKey)
    userPrivateJWK = await jose.exportJWK(userKeys.privateKey)
    userPublicJWK = await jose.exportJWK(userKeys.publicKey)

    // Browser-only initialization
    if (isBrowser()) {
        // Request persistent storage so the browser won't evict IndexedDB/keys.
        // Raced against a timeout: persist() has no spec-bounded resolution
        // time and a stalled promise here would hang acequiaReady() forever,
        // leaving every page gated on it (registration, dashboard) dead with
        // no error. Persistence is best-effort — losing it is survivable,
        // hanging boot is not.
        if (navigator.storage?.persist) {
            const persisted = await Promise.race([
                navigator.storage.persist(),
                new Promise((resolve) =>
                    setTimeout(() => resolve('timeout'), 3000)
                ),
            ])
            console.log(
                'Persistent storage:',
                persisted === 'timeout'
                    ? 'request timed out (continuing without)'
                    : persisted
                      ? 'granted'
                      : 'denied'
            )
        }

        // Request Local Network Access permission before registering service worker
        // This ensures the permission is granted before SW makes local network requests
        await requestLocalNetworkAccess()

        await registerServiceWorker()

        // Set up service worker message listener for proxied requests
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', async (event) => {
                const { data } = event
                if (data?.type === 'proxiedRequest') {
                    await handleProxiedRequest(data, event.source)
                } else if (data?.type === 'webrtcProxiedRequest') {
                    await handleWebRTCProxiedRequest(data, event.source)
                } else if (data?.type === 'SW_STATE') {
                    if (swStateResolve) {
                        swStateResolve(data.data)
                        swStateResolve = null
                    }
                } else if (data?.type === 'group:peerMessage') {
                    // SW delivered a peer message — route to the correct Group
                    const group = activeGroups.get(data.groupId)
                    if (group) {
                        group.fire('peerMessage', {
                            peerId: data.fromInstanceId,
                            message: data.message,
                            transport: 'sw',
                        })
                    }
                } else if (data?.type === 'group:peerMessageAck') {
                    const pending = pendingSWAcks.get(data.requestId)
                    if (pending) {
                        pendingSWAcks.delete(data.requestId)
                        clearTimeout(pending.timeout)
                        pending.resolve(data.delivered)
                    }
                } else if (data?.type === 'REQUEST_INSTANCE_REGISTRATION') {
                    // New SW activated — re-register this tab's instanceId
                    const myId = getInstanceId()
                    if (myId) {
                        console.log('SW requested instance re-registration, sending', myId)
                        postToSW({ type: 'REGISTER_INSTANCE', instanceId: myId, caps: swCaps() })
                    }
                } else if (data?.type === 'REQUEST_ROUTE_REGISTRY') {
                    // New SW activated — re-register all routes so the SW gets ROUTE_REGISTRY
                    console.log('SW requested route registries, re-registering for', activeGroups.size, 'group(s)')
                    for (const [, group] of activeGroups) {
                        const localRoutes = group.router?.getRouteRegistry() || {}
                        const routeEntries = Object.entries(localRoutes)
                        if (routeEntries.length > 0 && group.server?.readyState === WebSocket.OPEN) {
                            group.server.send(
                                JSON.stringify({
                                    type: 'registerRoutes',
                                    routes: routeEntries.map(([pattern, entry]) => ({
                                        pattern,
                                        metadata: entry.metadata || {},
                                    })),
                                    instanceId: getInstanceId(),
                                })
                            )
                        }
                    }
                }
            })
        }

        postToSW({ type: 'REGISTER_INSTANCE', instanceId, caps: swCaps() })
    } else if (isServiceWorker()) {
        // Service worker context - minimal initialization
        // SW shares IndexedDB with browser, so deviceId/userId/keys are the same
        // instanceId is ephemeral for the SW lifecycle
        console.log('Acequia initialized in Service Worker context')
    } else if (isDeno()) {
        // Deno context - filesystem-based storage
        const storageRoot = Deno.env.get('ACEQUIA_STORAGE') || './acequia-data'
        console.log('Acequia initialized in Deno context')
        console.log(`Storage root: ${storageRoot}`)
    } else if (isNode()) {
        // Node.js context - filesystem-based storage
        const storageRoot = process.env.ACEQUIA_STORAGE || './acequia-data'
        // stdout is a runner's data channel (yarn sync --json); say this only when asked
        if (/^(1|true|yes)$/i.test(process.env.ACEQUIA_DEBUG || '')) {
            console.error('Acequia initialized in Node.js context')
            console.error(`Storage root: ${storageRoot}`)
        }
    }

    makeReady({
        deviceId,
        userId,
        handle,
        deviceName,
        instanceId,
        instanceName,
        keys,
        privateJWK,
        publicJWK,
        userKeys,
        userPrivateJWK,
        userPublicJWK,
    })
}).catch((err) => {
    // A throw anywhere in boot would otherwise leave readyPromise pending
    // forever — every page awaiting acequiaReady() hangs with no error.
    // Resolve with whatever state exists plus the error so callers can
    // proceed (or surface it) instead of freezing.
    console.error('Acequia initialization failed:', err)
    makeReady({
        deviceId,
        userId,
        handle,
        deviceName,
        instanceId,
        instanceName,
        keys,
        privateJWK,
        publicJWK,
        userKeys,
        userPrivateJWK,
        userPublicJWK,
        error: err?.message || String(err),
    })
})

export function getDeviceId() {
    return deviceId
}

export function getUserId() {
    return userId
}

export function getKeys() {
    return keys
}

export function getUserKeys() {
    return userKeys
}

export function getInstanceId() {
    return instanceId
}

/**
 * Get the base URL for discovery server HTTP calls (group proxy, etc.).
 *
 * Reads `config.acequia.discoveryServer`:
 *   - number or numeric string → port-only (preserves domain from hostname)
 *   - URL string              → full URL override
 *   - empty / null            → same origin (Cloudflare tunnel default)
 *
 * Falls back to legacy discoveryPort / discoveryURL if discoveryServer is not set.
 */
export function getDiscoveryOrigin() {
    // Outside a window (Node, 2026-09-28) there is no page origin: a full
    // URL in config still answers; a port or nothing answers null, and the
    // caller (Group) must be given a discoveryURL explicitly.
    const hasWindow = typeof window !== 'undefined' && window.location
    const ds = config?.acequia?.discoveryServer
    if (ds != null && ds !== '') {
        const val = typeof ds === 'number' ? ds : ds.toString()
        if (typeof ds === 'number' || /^\d+$/.test(val)) {
            if (!hasWindow) return null
            return `${window.location.protocol}//${window.location.hostname}:${val}`
        }
        return val
    }

    // Legacy fallback — emit deprecation warning
    const port = config?.acequia?.discoveryPort
    if (port) {
        console.warn('acequia: config.acequia.discoveryPort is deprecated — use discoveryServer instead')
        if (!hasWindow) return null
        return `${window.location.protocol}//${window.location.hostname}:${port}`
    }
    const url = config?.acequia?.discoveryURL
    if (url) {
        console.warn('acequia: config.acequia.discoveryURL is deprecated — use discoveryServer instead')
        return url
    }

    return hasWindow ? window.location.origin : null
}

export function getHandle() {
    return handle
}

export function getDeviceName() {
    return deviceName
}

export function getInstanceName() {
    return instanceName
}

/**
 * Refresh user identity from IndexedDB.
 * Call this after device linking or any operation that updates userId/userKeys
 * to ensure the in-memory cache reflects the latest stored values.
 * @returns {Promise<Object>} Updated identity info
 */
export async function refreshUserIdentity() {
    // Reload from storage (IndexedDB in browser, filesystem in Node.js)
    userId = await acequiaStore.getItem('userId')
    handle = await acequiaStore.getItem('handle')
    userKeys = await acequiaStore.getItem('userKeys')

    if (userKeys) {
        // If stored as JWK (Node.js), import back to CryptoKey
        if (userKeys._jwk) {
            userKeys = {
                publicKey: await jose.importJWK(userKeys.publicKey, 'PS256', { extractable: true }),
                privateKey: await jose.importJWK(userKeys.privateKey, 'PS256', { extractable: true }),
            }
        }

        userPrivateJWK = await jose.exportJWK(userKeys.privateKey)
        userPublicJWK = await jose.exportJWK(userKeys.publicKey)

        if (typeof window !== 'undefined') {
            window.userKeys = userKeys
        }
    }

    console.log('User identity refreshed: userId=', userId, 'handle=', handle)

    return {
        userId,
        handle,
        userKeys,
        userPrivateJWK,
        userPublicJWK,
    }
}

/**
 * Check Local Network Access permission status (exported for UI use)
 * @returns {Promise<string>} Permission state: "granted", "denied", "prompt", or "unsupported"
 */
export { checkLocalNetworkAccessPermission }

/**
 * Manually request Local Network Access permission (exported for UI use)
 * Useful for user-initiated permission requests from settings pages
 */
export { requestLocalNetworkAccess }

/**
 * Resolve `true` when a service worker controls this page — immediately if
 * one already does, else on the next controllerchange (a normal first
 * visit's claim). Resolves `false` at once where service workers are
 * unavailable, and as soon as registration fails or proves unsupported
 * (serviceWorkerStatus() says why). NOTE: on a force-refreshed client this promise never
 * settles beyond the unavailable case — the spec flags such pages
 * uncontrollable for their lifetime and claim skips them — so callers
 * must own their own timeout (see the alert.live first-load bootstrap
 * for the reload-once pattern).
 */
export function swControlled() {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        return Promise.resolve(false)
    }
    if (navigator.serviceWorker.controller) return Promise.resolve(true)
    return new Promise((resolve) => {
        navigator.serviceWorker.addEventListener(
            'controllerchange',
            () => resolve(true),
            { once: true }
        )
        swOutcome.then((status) => {
            if (status.state !== 'registered') resolve(false)
        })
    })
}

export function postToSW(message) {
    // if (config.debug) console.log('post to SW: ', message)
    if (navigator?.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage(message)
    }
}

// Global registry to track active groups and their routers
export const activeGroups = new Map()

export function registerGroup(group) {
    activeGroups.set(group.groupId, group)
}

export function unregisterGroup(groupId) {
    activeGroups.delete(groupId)
}

// Pending SW state request resolvers
let swStateResolve = null

// Pending SW peer message delivery acks (requestId → { resolve, timeout })
const pendingSWAcks = new Map()

/**
 * Wait for the SW to acknowledge delivery of a group:peerMessage.
 * Resolves to true if delivered, false if target not found or timeout.
 */
export function awaitSWDelivery(requestId) {
    return new Promise(resolve => {
        const timeout = setTimeout(() => {
            pendingSWAcks.delete(requestId)
            resolve(false)
        }, 200)
        pendingSWAcks.set(requestId, { resolve, timeout })
    })
}

/**
 * Diagnose route registry state across browser, SW, and server.
 * Call from browser console: await acequia.diagnoseRoutes()
 */
export async function diagnoseRoutes() {
    const result = { browser: {}, sw: null, server: {}, issues: [] }

    // 1. Collect browser-side state
    for (const [groupId, group] of activeGroups) {
        result.browser[groupId] = {
            routes: Object.keys(group.router?.getRouteRegistry() || {}),
            peers: Object.keys(group.peersList || {}),
            wsState: group.server?.readyState,
        }
    }

    // 2. Request SW state
    try {
        const swState = await new Promise((resolve, reject) => {
            swStateResolve = resolve
            postToSW({ type: 'GET_SW_STATE' })
            setTimeout(() => { swStateResolve = null; reject(new Error('SW state timeout')) }, 3000)
        })
        result.sw = swState
    } catch (e) {
        result.issues.push(`SW state: ${e.message}`)
    }

    // 3. Fetch server state for each group. Discovery answers /admin to its
    // own host only (since 2026-10-20); localWebDAV relays it to a steward or
    // site admin of this domain, signed in by the auth cookie this fetch sends.
    const domain = encodeURIComponent(location.hostname)
    for (const groupId of activeGroups.keys()) {
        const groupPath = String(groupId).split('/').filter(Boolean).map(encodeURIComponent).join('/')
        try {
            const resp = await fetch(`/api/domain/${domain}/discovery/state/${groupPath}`, { cache: 'no-store' })
            if (resp.ok) {
                result.server[groupId] = await resp.json()
            } else if (resp.status === 401 || resp.status === 403) {
                result.issues.push(`Server state for ${groupId}: needs a steward or site admin of this domain (${resp.status})`)
            } else {
                result.issues.push(`Server state for ${groupId}: ${resp.status}`)
            }
        } catch (e) {
            result.issues.push(`Server fetch failed for ${groupId}: ${e.message}`)
        }
    }

    // 4. Compare routes across layers
    for (const groupId of activeGroups.keys()) {
        const browserRoutes = new Set(result.browser[groupId]?.routes || [])
        const swRoutes = new Set(Object.keys(result.sw?.groupRoutes?.[groupId] || {}))
        const serverRoutes = new Set(Object.keys(result.server[groupId]?.routes || {}))

        for (const r of browserRoutes) {
            if (!serverRoutes.has(r)) result.issues.push(`Route ${r} in browser but NOT on server (${groupId})`)
            if (!swRoutes.has(r)) result.issues.push(`Route ${r} in browser but NOT in SW (${groupId})`)
        }
        for (const r of serverRoutes) {
            if (!swRoutes.has(r)) result.issues.push(`Route ${r} on server but NOT in SW (${groupId})`)
        }
        for (const r of swRoutes) {
            if (!serverRoutes.has(r)) result.issues.push(`Route ${r} in SW but NOT on server (${groupId})`)
        }
    }

    // 5. Check for stale SW instances
    if (result.sw?.instanceMap) {
        const now = Date.now()
        for (const [id, inst] of Object.entries(result.sw.instanceMap)) {
            const age = now - inst.lastUpdate
            if (age > 120000) {
                result.issues.push(`Stale SW instance ${id}: last update ${Math.round(age / 1000)}s ago`)
            }
        }
    }

    console.log('Route Diagnosis:', result)
    if (result.issues.length === 0) {
        console.log('No issues found')
    } else {
        console.warn(`Found ${result.issues.length} issue(s):`, result.issues)
    }
    return result
}

/**
 * What this acequia can take from the SW's local dispatch. Request bodies
 * arrive as a Blob by default: in Chrome 153 a transferred stream moved a
 * 2.2 GB File PUT at 38–45 MB/s against 58–100 MB/s for the Blob, with
 * equally flat memory (stream-request-bodies-across-the-sw-postmessage-edge).
 * `config.acequia.localBodyStreams: true` opts a page into the live stream
 * for time-to-first-byte, and for upload progress feeding the SW's idle timer.
 */
function swCaps() {
    return { bodyStream: config.acequia?.localBodyStreams === true }
}

/**
 * Handle proxied requests from service worker
 * @param {object} request - The proxied request data
 * @param {MessagePort} source - The service worker source
 */
async function handleProxiedRequest(request, source) {
    const { requestId, path } = request

    try {
        // Find a group that can handle this request
        let matchedGroup = null
        let matchedRoute = null

        for (const [groupId, group] of activeGroups) {
            try {
                const match = group.router.matchRoute(path)
                if (match) {
                    matchedGroup = group
                    matchedRoute = match
                    break
                }
            } catch {
                // No matching route in this group — try the next one
            }
        }

        if (!matchedGroup || !matchedRoute) {
            // No matching route found — nothing will read a streamed body.
            request.bodyStream?.cancel().catch(() => {})
            source.postMessage({
                type: 'proxiedResponse',
                requestId,
                response: {
                    data: {
                        status: 404,
                        statusText: 'Not Found',
                        body: 'No route handler found',
                        headers: { 'content-type': 'text/plain' },
                    },
                },
            })
            return
        }

        // A Blob that arrived dead (Safari under memory pressure) would fail
        // the handler late with nothing written; say so up front.
        if (!(await postedBodyReadable(request))) {
            source.postMessage({ type: 'proxiedResponse', requestId, response: { data: bodyLostResponse() } })
            return
        }

        // Execute the route handler. Streaming routes read the body as a
        // stream; the rest get it buffered and normalized, as always.
        const { body, rawBody, bodyStream } = await prepareProxiedBody(request, {
            streaming: matchedRoute.metadata?.streaming === true,
            normalizeBody,
        })
        // Pings keep the SW's idle timer alive while the handler works past
        // the last body byte (an FSA flush, a MOVE of a large tree).
        const stopPings = startProgressPings((m) => source.postMessage(m), requestId)
        let responseData
        try {
            responseData = await matchedRoute.handler({
                ...request,
                body,
                rawBody,
                bodyStream, // replaces the posted stream: consumed above unless streaming
                pathParams: matchedRoute.pathParams,
                remainingPath: matchedRoute.remainingPath,
            })
        } finally {
            stopPings()
            // A handler that answered without reading the body (a 401, a
            // 404): release the upload rather than leave the SW's pipe
            // waiting on a reader that never comes.
            if (bodyStream && !bodyStream.locked) bodyStream.cancel().catch(() => {})
        }

        // Send response back to service worker. A ReadableStream body (a
        // streaming-route handler on this same device) must ride the
        // transfer list — it cannot be structured-cloned.
        const message = {
            type: 'proxiedResponse',
            requestId,
            response: {
                data: responseData,
            },
        }
        if (typeof ReadableStream !== 'undefined' && responseData?.body instanceof ReadableStream) {
            source.postMessage(message, [responseData.body])
        } else {
            source.postMessage(message)
        }
    } catch (error) {
        console.error('Error handling proxied request:', error)

        // Send error response back to service worker
        source.postMessage({
            type: 'proxiedResponse',
            requestId,
            response: {
                data: {
                    status: 500,
                    statusText: 'Internal Server Error',
                    body: error.message,
                    headers: { 'content-type': 'text/plain' },
                },
            },
        })
    }
}

/**
 * Handle WebRTC proxied requests from service worker
 * @param {object} data - Contains targetInstanceId and request
 * @param {MessagePort} source - The service worker source
 */
async function handleWebRTCProxiedRequest(data, source) {
    const { targetInstanceId, request, groupId: requestGroupId } = data
    const { requestId } = request

    try {
        // Find a group that has a peer connection to the target instance.
        // Prefer the group specified by the service worker (the group whose
        // route registry matched the request path) to avoid matching the
        // wrong group's router when a peer belongs to multiple groups.
        let targetPeer = null
        let targetGroup = null

        if (requestGroupId && activeGroups.has(requestGroupId)) {
            const group = activeGroups.get(requestGroupId)
            const peer = group.getPeerByInstanceId?.(targetInstanceId)
            if (peer) {
                targetPeer = peer
                targetGroup = group
            }
        }

        // Fallback: iterate all groups (backwards compat with older SW)
        if (!targetPeer) {
            for (const [groupId, group] of activeGroups) {
                const peer = group.getPeerByInstanceId?.(targetInstanceId)
                if (peer) {
                    targetPeer = peer
                    targetGroup = group
                    break
                }
            }
        }

        // If no peer exists, try to create one
        if (!targetPeer) {
            const groupsToTry = requestGroupId && activeGroups.has(requestGroupId)
                ? [[requestGroupId, activeGroups.get(requestGroupId)]]
                : activeGroups
            for (const [groupId, group] of groupsToTry) {
                const peerInfo =
                    group.getPeerInfoByInstanceId?.(targetInstanceId)
                if (peerInfo) {
                    targetPeer = await group.connectToPeer(targetInstanceId)
                    targetGroup = group
                    break
                }
            }
        }

        if (!targetPeer) {
            console.warn(`[route-diag] handleWebRTCProxiedRequest: no peer for ${targetInstanceId?.slice(0, 8)}, activeGroups=${[...activeGroups.keys()].join(',')}`)
            throw new Error(
                `No peer connection available for instance: ${targetInstanceId}`
            )
        }

        // Wait for peer to be ready
        const usedFallback = targetGroup?.groupId !== requestGroupId
        console.log(`[route-diag] handleWebRTCProxiedRequest: target=${targetInstanceId?.slice(0, 8)}, requestedGroup=${requestGroupId}, peerGroup=${targetGroup?.groupId}${usedFallback ? ' (cross-group fallback)' : ''}, channel=${targetPeer._channel?.readyState}, path=${request.path}`)
        await targetPeer.ready

        // Send request via WebRTC and wait for response.
        // Always forward the originally-requested groupId so the receiver
        // looks up routes in the correct group, even when the peer connection
        // was found via a different group (e.g. acequia/users fallback).
        const response = await targetGroup.sendRequestViaPeer(
            targetPeer,
            request,
            // preferStreamBody: the SW consumer builds its Response from a
            // ReadableStream directly, so streamed peer responses resolve
            // at 'start' with a live body (true time-to-first-byte).
            { groupId: requestGroupId, preferStreamBody: true }
        )

        // Send response back to service worker. A ReadableStream body must
        // ride the transfer list — it cannot be structured-cloned.
        const respBody = response?.data?.body
        if (typeof ReadableStream !== 'undefined' && respBody instanceof ReadableStream) {
            source.postMessage(
                { type: 'webrtcProxiedResponse', requestId, response },
                [respBody]
            )
        } else {
            source.postMessage({
                type: 'webrtcProxiedResponse',
                requestId,
                response,
            })
        }
    } catch (error) {
        console.error('Error handling WebRTC proxied request:', error)

        // Send error response back to service worker
        source.postMessage({
            type: 'webrtcProxiedResponse',
            requestId,
            error: error.message,
        })
    }
}
